import { useEffect, useMemo, useState } from 'react';
import type { MinigameViewProps } from '../../types/minigame';

// Virus Spread (level 40) — the turn-based rung. No clock at all: the whole difficulty is the plan,
// so there is no ticker, no polling and nothing anchored to the wall clock in here.
//
// The network is a TREE, which is what makes it renderable as an indented list: every host has one
// way in, so the parent is simply the neighbour one hop closer to the sweep's origin. That also makes
// junctions read at a glance, and junctions are the whole game.

interface Host {
  id: number;
  name: string;
  depth: number;
  /** Turn the sweep arrives, or -1 for a host it never reaches. */
  sweptAtTurn: number;
  links: number[];
}

interface Challenge {
  started: boolean;
  hostCount: number;
  turn: number;
  budget: number;
  target: number;
  reach: number;
  origin: number;
  cloaksLeft: number;
  cloaksGranted: number;
  sweepHopsPerTurn: number;
  hosts: Host[];
  infected: number[];
  immune: number[];
  cloaked: number[];
}

export default function VirusSpreadView({
  challenge, onAct, onSubmit, busy,
}: MinigameViewProps<Challenge, Challenge>) {
  const [live, setLive] = useState<Challenge | null>(challenge ?? null);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => { setLive(challenge ?? null); setNote(null); }, [challenge]);

  const hosts = useMemo(() => live?.hosts ?? [], [live]);
  const infected = useMemo(() => new Set(live?.infected ?? []), [live]);
  const immune = useMemo(() => new Set(live?.immune ?? []), [live]);
  const cloaked = useMemo(() => new Set(live?.cloaked ?? []), [live]);
  const started = live?.started ?? false;
  const turn = live?.turn ?? 1;

  const send = async (action: unknown) => {
    setNote(null);
    const next = await onAct(action);
    if (next) setLive(next);
  };

  // Depth-first from the origin, so the list reads as the network's shape. The parent of a host is
  // its neighbour one hop closer in — unique, because the network is a tree.
  const ordered = useMemo(() => {
    if (hosts.length === 0) return [] as Host[];
    const byId = new Map(hosts.map(h => [h.id, h]));
    const out: Host[] = [];
    const walk = (id: number, from: number) => {
      const h = byId.get(id);
      if (!h) return;
      out.push(h);
      for (const l of h.links) if (l !== from) walk(l, id);
    };
    walk(live?.origin ?? 0, -1);
    return out;
  }, [hosts, live]);

  const row: React.CSSProperties = { fontFamily: 'monospace', fontSize: '0.8rem' };
  const held = infected.size;
  const target = live?.target ?? 0;
  const budget = live?.budget ?? 0;

  if (!started) {
    return (
      <div style={{ fontFamily: 'monospace' }}>
        <div style={{ border: '1px solid #232833', borderRadius: 4, padding: '0.9rem', background: '#0d1117' }}>
          <p style={{ ...row, color: '#cbd5e1', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            A network of <strong>{live?.hostCount ?? 0} hosts</strong>, and your strain is already on one
            of them. An <strong style={{ color: '#f87171' }}>anti-virus sweep</strong> starts at one end
            and moves outward <strong>one hop per turn</strong>. Every host shows the turn it arrives.
          </p>
          <p style={{ ...row, color: '#cbd5e1', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            The sweep <strong>sterilises</strong>. A host it finds infected is cleaned and immunised for
            good — you can never take it again. A host it finds <em>clean</em> it simply passes over, and
            that host is then <strong>safe forever</strong>. Its reach is limited, so the far end of the
            network is a haven it never touches at all.
          </p>
          <p style={{ ...row, color: '#cbd5e1', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            One action per turn: <strong>infect</strong> a host next to one of yours, or{' '}
            <strong>cloak</strong> one of yours so it survives the pass. Hold{' '}
            <strong>enough hosts</strong> when the turns run out.
          </p>
          <p style={{ ...row, color: '#60a5fa', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            The catch worth thinking about: you spread from any host you hold, so losing your tail costs
            you nothing — <em>unless</em> it was the only way into somewhere. Every host has one route
            in, so a junction sterilised while you are out on one of its branches takes the other
            branches with it. That is what a cloak is really for.
          </p>
          {(live?.cloaksGranted ?? 0) > 0 ? (
            <p style={{ ...row, color: '#60a5fa', margin: '0 0 0.8rem' }}>
              Your Trojan affords{' '}
              <strong>{live?.cloaksGranted} cloak{(live?.cloaksGranted ?? 0) === 1 ? '' : 's'}</strong> for
              the whole run. Applying one still costs the turn.
            </p>
          ) : (
            <p style={{ ...row, color: '#fbbf24', margin: '0 0 0.8rem' }}>
              Your Trojan is below the version this needs: <strong>no cloaks at all</strong>. Everything
              you keep has to be taken after the sweep has already been past it.
            </p>
          )}
          <button
            className="primary"
            onClick={() => void send({ kind: 'begin' })}
            disabled={busy}
            style={{ fontSize: '0.85rem', padding: '0.4rem 1rem' }}
          >
            Release the strain
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'monospace' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap',
        border: '1px solid var(--border-color)', borderRadius: 4, padding: '0.5rem 0.7rem',
        marginBottom: '0.6rem', background: '#0d1117',
      }}>
        <span style={{ ...row, minWidth: '7rem' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>TURN </span>
          <strong style={{ color: turn > budget - 2 ? '#fbbf24' : '#cbd5e1' }}>{Math.min(turn, budget)}</strong>
          <span style={{ color: '#4b5563' }}> / {budget}</span>
        </span>
        <span style={{ ...row, minWidth: '8rem' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>HELD </span>
          <strong style={{ color: held >= target ? '#4ade80' : '#cbd5e1' }}>{held}</strong>
          <span style={{ color: '#4b5563' }}> / {target} needed</span>
        </span>
        <span style={{ ...row, minWidth: '6.5rem', color: (live?.cloaksLeft ?? 0) > 0 ? '#60a5fa' : '#6b7280' }}>
          cloaks {live?.cloaksLeft ?? 0}/{live?.cloaksGranted ?? 0}
        </span>
        <span style={{ ...row, fontSize: '0.72rem', color: '#6b7280' }}>
          sweep reaches {live?.reach ?? 0} hops · beyond that is haven
        </span>
      </div>

      <div style={{ display: 'grid', gap: '0.15rem', marginBottom: '0.6rem' }}>
        {ordered.map(h => {
          const isMine = infected.has(h.id);
          const isGone = immune.has(h.id);
          const isCloaked = cloaked.has(h.id);
          const haven = h.sweptAtTurn < 0;
          const passed = !haven && h.sweptAtTurn < turn;
          const dueNow = h.sweptAtTurn === turn;
          const canInfect = !isMine && !isGone
            && h.links.some(l => infected.has(l));
          const canCloak = isMine && !isCloaked && !haven && h.sweptAtTurn >= turn;

          return (
            <div key={h.id} style={{
              ...row, display: 'flex', alignItems: 'center', gap: '0.5rem',
              paddingLeft: `${h.depth * 1.1}rem`,
              opacity: isGone ? 0.45 : 1,
            }}>
              <span style={{
                minWidth: '4.2rem', fontSize: '0.7rem', textAlign: 'right',
                color: haven ? '#4ade80' : dueNow ? '#f87171' : passed ? '#4b5563' : 'var(--text-muted)',
              }}>
                {haven ? 'HAVEN' : passed ? 'passed' : `t=${h.sweptAtTurn}`}
              </span>
              <span style={{
                minWidth: '1rem',
                color: isGone ? '#7f1d1d' : isMine ? (isCloaked ? '#60a5fa' : '#4ade80') : '#4b5563',
              }}>
                {isGone ? '✕' : isMine ? '◉' : '○'}
              </span>
              <span style={{
                minWidth: '8rem',
                color: isGone ? '#6b7280' : isMine ? '#e2e8f0' : '#9aa4b2',
                textDecoration: isGone ? 'line-through' : undefined,
              }}>
                {h.name}
              </span>
              <span style={{ minWidth: '4.5rem', fontSize: '0.7rem', color: '#60a5fa' }}>
                {isCloaked ? 'cloaked' : ''}
              </span>
              <span style={{ display: 'flex', gap: '0.3rem' }}>
                {canInfect && (
                  <button
                    onClick={() => void send({ kind: 'infect', host: h.id })}
                    disabled={busy}
                    style={{ ...row, fontSize: '0.7rem', padding: '0.05rem 0.4rem' }}
                  >
                    infect
                  </button>
                )}
                {canCloak && (live?.cloaksLeft ?? 0) > 0 && (
                  <button
                    onClick={() => void send({ kind: 'cloak', host: h.id })}
                    disabled={busy}
                    style={{ ...row, fontSize: '0.7rem', padding: '0.05rem 0.4rem', color: '#60a5fa' }}
                    title="Survives the sweep's pass"
                  >
                    cloak
                  </button>
                )}
              </span>
            </div>
          );
        })}
      </div>

      {/* A reserved slot, so a refusal never moves the buttons under it. */}
      <div style={{ height: '1.2rem', marginBottom: '0.4rem', overflow: 'hidden' }}>
        {note && <span style={{ ...row, fontSize: '0.76rem', color: '#fbbf24' }}>{note}</span>}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          onClick={() => void send({ kind: 'pass' })}
          disabled={busy}
          style={{ ...row, fontSize: '0.8rem', padding: '0.3rem 0.7rem' }}
          title="Let the sweep advance without acting"
        >
          Wait a turn
        </button>
        <button
          className="primary"
          onClick={() => onSubmit({})}
          disabled={busy}
          style={{ fontSize: '0.8rem', padding: '0.3rem 0.8rem' }}
          title="Count what you hold and stop here"
        >
          Stop here
        </button>
        <span style={{ ...row, fontSize: '0.72rem', color: '#4b5563' }}>
          ✕ sterilised — can never be retaken
        </span>
      </div>
    </div>
  );
}
